export interface DetailedBullet {
  title: string
  detail: string
}

export interface Bonus {
  id: string
  title: string
  subtitle: string
  /** Reference value in USD cents, shown struck through next to "Included free" */
  referenceValueCents?: number
  intro?: string
  bulletsIntro?: string
  bullets?: string[]
  detailedBullets?: DetailedBullet[]
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  secondaryNote?: string
  systemLabel?: string
  system?: string
  result?: string
  closing?: string[]
}

export const bonuses: Bonus[] = [
  {
    id: '01',
    title: 'Sewing Techniques & Stitch Reference Flashcards',
    subtitle: 'Fast, Accurate Recall Before Every Class and Workshop',
    referenceValueCents: 14990,
    intro:
      'Printable visual flashcards with a clear labeled illustration on the front and the key facts, a quick-check question and an instructor cue on the back.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Sewing-machine parts',
      'The threading path',
      'Needle sizes by fabric (60/8 to 110/18)',
      'Stitch types & stitch lengths',
      'Seam types & seam finishes',
      'Hand stitches',
      'Pattern symbols',
      'Fabric grain & common fabrics',
      'Beginner, intermediate & advanced decks',
    ],
    result: 'Use them for warm-ups, quick reviews, station rotations and quizzes at every level.',
  },
  {
    id: '02',
    title: 'Sewing Demonstration Lesson Scripts',
    subtitle: 'Run a Polished Demonstration Without Preparing From Scratch',
    referenceValueCents: 9990,
    intro:
      'Ready-to-read demonstration scripts that follow the 4-step method (show, explain, student explains, student does), each with timing, materials list, safety reminders, key instructor phrases, common student mistakes and a quick check.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Threading the machine & winding the bobbin',
      'Sewing a straight 5/8 in (1.5 cm) seam',
      'Pivoting corners & sewing curves',
      'Pressing seams',
      'Sewing darts',
      'Centered & invisible zippers',
      'Setting in a sleeve',
      'Attaching a waistband & hemming',
    ],
    result: 'Teach every demonstration with the confidence of a script that already works.',
  },
  {
    id: '03',
    title: 'Pattern Making Practice Drills & Quarter-Scale Exercises',
    subtitle: 'Build Accurate Drafting Skills Step by Step',
    referenceValueCents: 7990,
    intro:
      'A practice workbook of pattern-making drills, each with clear instructions, a worked example, a blank practice space and an answer key, ready to assign in class or as homework.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Reading & labeling pattern symbols',
      'Adding seam allowances',
      'Finding the grainline',
      'Measuring a dress form & recording a measurement chart',
      'Quarter-scale skirt & bodice blocks',
      'Dart manipulation: slash-and-spread & pivot',
      'Simple pattern adjustments',
      'Grading practice',
    ],
    result: 'Give students the repetition they need to draft with precision and confidence.',
  },
  {
    id: '04',
    title: 'Fitting & Alterations Troubleshooting Cards',
    subtitle: 'Diagnose Every Fit Problem and Know Exactly How to Fix It',
    referenceValueCents: 6990,
    intro:
      'Visual troubleshooting cards that show each problem on a dress form, the likely cause, the pattern adjustment and the alteration on a finished garment, with an instructor tip and a practice exercise.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Reading horizontal, vertical & diagonal wrinkles',
      'Gaping necklines & armholes',
      'Tight bust or hips',
      'Sway back',
      'Sloping shoulders',
      'Too-long or too-short crotch',
      'Pulling buttons',
      'Wavy hems',
    ],
    result: 'Help students read a toile and solve fit problems with a clear, logical method.',
  },
  {
    id: '05',
    title: 'Sewing Competency Checklists, Rubrics & Certificates',
    subtitle: 'Evaluate Objectively and Document Every Skill Achieved',
    referenceValueCents: 5990,
    intro:
      'Ready-to-print assessment tools for sewing instructors and academies: competency checklists for beginner, intermediate and advanced levels, scoring rubrics for sewing samples and finished garments, a student progress tracker and course-completion certificates for each level.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Machine skills',
      'Seams & finishes',
      'Zippers & closures',
      'Collars & sleeves',
      'Drafting & fitting',
      'Tailoring',
      'Sample & garment rubrics',
      'Progress tracker & certificates',
    ],
    result: 'Assess every student fairly and give structured feedback at every level.',
  },
]
